// One dialog remains mounted while our durable order goes from confirming to
// granted. A return URL is an arrival signal, never proof of payment.
import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import Modal from '../../components/ui/Modal'
import { api, ApiRequestError } from '../../api/client'
import { useAuth } from '../../contexts/AuthContext'
import { trackCheckoutReturnFail, trackCheckoutReturnSuccess, trackCheckoutReturnUnmatched } from '../../analytics/events'
import { createNadDebug } from '../../utils/debugLog'
import { clearPendingCheckout } from './checkoutReturnStore'
import { formatCredits, formatMoney, type CheckoutOrder } from './billingTypes'
import type { CheckoutResultState } from './checkoutResultState'
import { checkoutWalletLabel } from './receipt'
import SandboxNotice from './SandboxNotice'

const log = createNadDebug('dw:billing-return')
const ongoing = new Set(['created', 'handoff_issued', 'returned_success', 'returned_failure', 'payment_verified', 'granting'])
const primary = 'h-9 rounded-lg bg-foreground px-4 text-base font-medium text-background hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary'

export default function CheckoutResultDialog({ result, onClose }: { result: CheckoutResultState; onClose: () => void }) {
  const { t, i18n } = useTranslation('settings')
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [order, setOrder] = useState<CheckoutOrder | null>(null)
  const [phase, setPhase] = useState<'confirming' | 'order' | 'unmatched' | 'lookupFailed' | 'pending'>('confirming')
  const [receiptOpen, setReceiptOpen] = useState(false)
  const reported = useRef(false)

  useEffect(() => {
    let disposed = false
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined
    const deadline = Date.now() + 30_000
    const deadlineTimer = setTimeout(() => {
      if (timer) clearTimeout(timer)
      setPhase(current => current === 'confirming' ? 'pending' : current)
    }, 30_000)
    const report = (found: CheckoutOrder | null, lookupFailed: boolean) => {
      if (reported.current || disposed) return
      reported.current = true
      const knownId = found?.orderId ?? (lookupFailed ? result.orderId : null)
      log({ event: `checkout:return_${result.outcome}`, trace_id: knownId ? `billing:${knownId}` : 'billing:unmatched', matched: found !== null })
      if (knownId === null) trackCheckoutReturnUnmatched({ has_order_id: result.orderId !== null, reason: result.orderId === null ? 'unknown_order' : 'foreign_order' })
      else if (result.outcome === 'success') trackCheckoutReturnSuccess({ mft_order_id: knownId })
      else trackCheckoutReturnFail({ mft_order_id: knownId })
    }
    const read = async () => {
      if (result.orderId === null) { setPhase('unmatched'); report(null, false); return }
      try {
        const found = await api.getCheckoutOrder(result.orderId, controller.signal)
        if (disposed) return
        report(found, false)
        setOrder(found)
        if (found.status === 'granted' || found.status === 'failed' || found.status === 'abandoned') {
          setPhase('order')
          clearTimeout(deadlineTimer)
          clearPendingCheckout()
          if (found.status === 'granted') {
            void queryClient.invalidateQueries({ queryKey: ['sparks'] })
            void queryClient.invalidateQueries({ queryKey: ['billing', 'credit-history'] })
            void queryClient.invalidateQueries({ queryKey: ['teams'] })
          }
          return
        }
        if (!ongoing.has(found.status)) throw new Error(`Unexpected checkout status: ${found.status}`)
        if (Date.now() >= deadline) { setPhase('pending'); return }
        setPhase('confirming')
        timer = setTimeout(() => { void read() }, 2000)
      } catch (error) {
        if (disposed) return
        const missing = error instanceof ApiRequestError && error.status === 404
        log({ event: 'checkout:lookup_failed', trace_id: 'billing:return', missing })
        report(null, !missing)
        setPhase(missing ? 'unmatched' : 'lookupFailed')
      }
    }
    void read()
    return () => { disposed = true; controller.abort(); clearTimeout(deadlineTimer); if (timer) clearTimeout(timer) }
  }, [queryClient, result.orderId, result.outcome, user?.id])

  const granted = phase === 'order' && order?.status === 'granted'
  const failed = phase === 'order' && !granted
  const confirming = phase === 'confirming'
  const centered = confirming || granted
  const title = granted ? t(order?.paymentContainerMode === 'LIVE' ? 'settingsModal.billing.paymentSuccessful' : 'settingsModal.billing.creditsAddedTitle')
    : failed ? t('settingsModal.billing.paymentNotCompleted')
    : confirming ? t('settingsModal.billing.confirmingTitle')
    : phase === 'pending' ? t('settingsModal.billing.stillConfirming')
    : phase === 'unmatched' ? t('settingsModal.billing.returnUnmatchedTitle') : t('settingsModal.billing.returnLookupTitle')
  const body = failed ? t('settingsModal.billing.paymentNotConfirmedBody')
    : confirming ? t('settingsModal.billing.confirmingBody')
    : phase === 'pending' ? t('settingsModal.billing.stillConfirmingBody')
    : phase === 'unmatched' ? t('settingsModal.billing.returnUnmatchedOrder') : t('settingsModal.billing.returnLookupFailed')

  return <Modal isOpen onClose={onClose} title={title} hideTitle={centered} size="billing-result" titleClassName="text-lg font-semibold" padded={false} surfaceClassName="rounded-xl border border-border bg-surface-2 shadow-2xl" overlayClassName="z-[1200]">
    <div data-testid={result.outcome === 'success' ? 'checkout-return-success' : 'checkout-return-failure'}>
      <div aria-live="polite" className={centered ? 'flex flex-col items-center gap-4 px-6 py-6 text-center' : 'flex flex-col gap-3 p-5'}>
        {confirming && <Loader2 className="h-8 w-8 animate-spin text-foreground" aria-hidden="true" />}
        {granted && <CheckCircle2 className="h-8 w-8 text-foreground" aria-hidden="true" strokeWidth={1.75} />}
        {centered && <h2 className="text-lg font-semibold text-foreground">{title}</h2>}
        {order?.paymentContainerMode === 'TEST' && !granted && <SandboxNotice />}
        {failed ? <div role="alert" className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-base text-destructive"><AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />{body}</div>
          : !granted && <p className="text-base text-muted-foreground" data-testid="checkout-return-pending-credits">{body}</p>}
        {!granted && !failed && order?.destination && <p className="text-base text-muted-foreground" data-testid="checkout-return-destination">{t('settingsModal.billing.destination')}: {checkoutWalletLabel(order.destination, t)}</p>}
        {granted && order && <>
          <p className="text-base text-muted-foreground">{t('settingsModal.billing.creditsAddedBody', { credits: formatCredits(order.credits, i18n.language), wallet: checkoutWalletLabel(order.destination, t) })}</p>
          <div className="w-full overflow-hidden rounded-lg border border-border text-left">
            <button type="button" aria-expanded={receiptOpen} aria-controls="checkout-receipt" onClick={() => setReceiptOpen(value => !value)} className="flex w-full items-center justify-between px-4 py-3 text-base font-medium text-foreground hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
              {t(receiptOpen ? 'settingsModal.billing.hideReceipt' : 'settingsModal.billing.showReceipt')}
              {receiptOpen ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
            </button>
            <div id="checkout-receipt" hidden={!receiptOpen} className="border-t border-border p-4" data-testid="checkout-return-order">
              <dl className="flex flex-col gap-3 text-base">
                <div className="flex justify-between gap-3" data-testid="checkout-return-destination"><dt className="text-muted-foreground">{t('settingsModal.billing.creditedWallet')}</dt><dd className="min-w-0 break-words text-right text-foreground">{checkoutWalletLabel(order.destination, t)}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('settingsModal.billing.credits')}</dt><dd className="text-foreground">{t('settingsModal.billing.packLabel', { credits: formatCredits(order.credits, i18n.language) })}</dd></div>
                {order.price && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t('settingsModal.billing.amountPaid')}</dt><dd className="text-foreground">{formatMoney(order.price, i18n.language)}</dd></div>}
              </dl>
            </div>
          </div>
          <button type="button" data-testid="checkout-return-back" onClick={onClose} className={`${primary} w-full`}>{t('settingsModal.billing.close')}</button>
        </>}
      </div>
      {!centered && <div className="flex justify-end border-t border-border p-4"><button type="button" data-testid="checkout-return-back" onClick={onClose} className={primary}>{t('settingsModal.billing.done')}</button></div>}
    </div>
  </Modal>
}
